export type DeliveryPrecision = 'DATE' | 'DATETIME';

import type { OrderReadiness } from 'src/typings';

export type DeliveryTone = 'later' | 'tomorrow' | 'today' | 'overdue' | 'pending' | 'ready' | 'muted';

export interface DeliveryHint {
  label: string;
  title: string;
  tone: DeliveryTone;
  icon: string;
}

const dateFormat = new Intl.DateTimeFormat('ru-RU', {
  timeZone: 'Europe/Kiev', day: '2-digit', month: '2-digit', year: 'numeric',
  hour: '2-digit', minute: '2-digit', hour12: false
});

export function deliveryDate(value?: string | Date | null, precision?: DeliveryPrecision | null) {
  if (!value) return null;
  const date = value instanceof Date ? new Date(value.getTime()) : new Date(value);
  if (!Number.isFinite(date.getTime())) return null;
  const parts = dateFormat.formatToParts(date);
  const part = (type: string) => parts.find(item => item.type === type)?.value || '';
  const day = `${part('day')}.${part('month')}`;
  const hasTime = precision !== 'DATE' && (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value));
  const time = hasTime ? ` ${part('hour')}:${part('minute')}` : precision === 'DATE' ? ' · до конца дня' : '';
  return {
    timestamp: date.getTime(), short: `${day}${time}`, full: `${day}.${part('year')}${time}`,
    // Compare calendar days in Kyiv, including days when the UTC offset changes.
    calendarDay: Date.UTC(Number(part('year')), Number(part('month')) - 1, Number(part('day'))) / 86400000,
    hasTime
  };
}

export function deliveryDeadline(value?: string | Date | null, now = Date.now(), precision?: DeliveryPrecision | null) {
  const date = deliveryDate(value, precision);
  const current = deliveryDate(new Date(now));
  let tone: DeliveryTone = 'muted';
  let label = 'Срок не указан';
  let icon = 'help_outline';
  if (date && current) {
    const days = date.calendarDay - current.calendarDay;
    if (days < 0 || (date.hasTime && date.timestamp < now)) {
      tone = 'overdue'; label = 'Задержка'; icon = 'schedule';
    } else if (days === 0) {
      tone = 'today'; label = 'Сегодня'; icon = 'today';
    } else if (days === 1) {
      tone = 'tomorrow'; label = 'Завтра'; icon = 'event';
    } else {
      tone = 'later'; label = `Через ${days} дн.`; icon = 'date_range';
    }
  }
  return { date, tone, label, icon };
}

export interface OrderDeliveryReference {
  readiness?: OrderReadiness;
  expected_at_precision?: DeliveryPrecision;
  eta_postponed?: boolean;
  eta_previous_expected_at?: string | Date | null;
  eta_current_expected_at?: string | Date | null;
  eta_previous_precision?: DeliveryPrecision | null;
  eta_current_precision?: DeliveryPrecision | null;
  eta_postponement_reason?: string | null;
  date_expected?: Date | string | null;
  supply_status?: string;
  shipment_status?: string;
  is_canceled?: boolean;
}

export function orderDeliveryHint(order: OrderDeliveryReference, now = Date.now()): DeliveryHint & { status: string } {
  const readiness = order.readiness;
  const active = !order.is_canceled && order.shipment_status !== 'FULLY_SHIPPED';
  if (active && readiness && readiness.status !== 'DATED') {
    if (readiness.status === 'IN_STOCK') return {
      label: 'На складе', status: 'Поставка не требуется', tone: 'ready', icon: 'inventory_2',
      title: 'Все оставшиеся товары обеспечены на складе заказа'
    };
    const reason = readiness.status === 'UNSOURCED' ? 'Не все товары обеспечены' :
      readiness.status === 'TRANSFER_PENDING' ? 'Есть перемещение' :
      readiness.status === 'DATE_MISSING' ? 'Нет срока поставки' : 'Нет товаров';
    return { label: 'Срок уточняется', status: reason, tone: 'pending', icon: 'help_outline',
      title: `Срок готовности заказа неизвестен: ${reason.toLowerCase()}` };
  }
  const deadline = deliveryDeadline(active && readiness ? readiness.expected_at : order.date_expected, now, order.expected_at_precision);
  let tone: DeliveryTone = deadline.tone;
  let icon = deadline.icon;
  let status = deadline.label;
  if (order.is_canceled) {
    tone = 'muted'; status = 'Отменён'; icon = 'block';
  } else if (order.shipment_status === 'FULLY_SHIPPED') {
    tone = 'muted'; status = 'Отгружено'; icon = 'done_all';
  } else if (order.supply_status === 'READY') {
    tone = 'ready'; status = 'На складе'; icon = 'inventory_2';
  }
  const previousEta = order.eta_postponed
    ? deliveryDate(order.eta_previous_expected_at, order.eta_previous_precision)
    : null;
  const currentEta = order.eta_postponed
    ? deliveryDate(order.eta_current_expected_at, order.eta_current_precision)
    : null;
  const postponement = order.eta_postponed
    ? ` · Срок перенесён${previousEta && currentEta ? `: ${previousEta.full} → ${currentEta.full}` : ''}${
        order.eta_postponement_reason ? ` · Причина: ${order.eta_postponement_reason}` : ''
      }`
    : '';
  return {
    label: deadline.date?.full || 'Срок не указан', status, tone, icon,
    title: `Срок поставки заказа: ${deadline.date?.full || 'не указан'} · ${status}${postponement}`
  };
}
